import { Pencil, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  CUSTOMER_CATEGORY_TYPES,
  type CustomerCategory,
  type CustomerCategoryType,
} from '@/types/customer-category'

interface CustomerCategoryTableProps {
  categories: CustomerCategory[]
  onEdit: (category: CustomerCategory) => void
  onDelete: (category: CustomerCategory) => void
}

const typeLabelMap = CUSTOMER_CATEGORY_TYPES.reduce<Record<CustomerCategoryType, string>>((map, item) => {
  map[item.value] = item.label
  return map
}, {} as Record<CustomerCategoryType, string>)

function formatDateTime(value?: number | null) {
  if (!value) return '-'
  const date = new Date(value * 1000)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString()
}

export function CustomerCategoryTable({ categories, onEdit, onDelete }: CustomerCategoryTableProps) {
  if (categories.length === 0) {
    return (
      <div className="rounded-lg border py-12 text-center text-sm text-muted-foreground">
        暂无客户类型
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-lg border bg-background">
      <Table className="min-w-[760px]">
        <TableHeader>
          <TableRow>
            <TableHead className="w-20">ID</TableHead>
            <TableHead>名称</TableHead>
            <TableHead className="w-32">类型种类</TableHead>
            <TableHead>备注</TableHead>
            <TableHead className="w-48">创建时间</TableHead>
            <TableHead className="w-28 text-right">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {categories.map((category) => (
            <TableRow key={category.id} className="hover:bg-muted/40">
              <TableCell className="font-mono text-xs text-muted-foreground">{category.id}</TableCell>
              <TableCell>
                <p className="font-medium">{category.name}</p>
              </TableCell>
              <TableCell>
                <Badge variant="secondary">{typeLabelMap[category.type] ?? category.type}</Badge>
              </TableCell>
              <TableCell className="max-w-md truncate text-muted-foreground">
                {category.description || '-'}
              </TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground">
                {formatDateTime(category.created_at)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => onEdit(category)}
                  >
                    <Pencil className="h-4 w-4" />
                    <span className="sr-only">编辑</span>
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => onDelete(category)}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">删除</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
